import { setContext, getContext } from 'svelte'
import { get, readable } from 'svelte/store'

import { TRANSLATIONS, getLanguage } from '$lib/context.js'

/** @typedef {{ [key: string]: { [key: string]: string|{ [key: string]: string } }}} Translation */
/** @typedef {function(TemplateStringsArray|string, ...any): string} Text */

/**
 * @param {...Translation} translations
 */
export function register(...translations) {
  const parent = getContext(TRANSLATIONS) || []
  setContext(TRANSLATIONS, [...translations, ...parent])
}

/**
 * Utility for use when context isn't available
 * @param {string} language
 */
export function createText(language) {
  return function text(strings, ...values) {
    const translations = getContext(TRANSLATIONS) || []
    return translate(strings, values, translations, language)
  }
}

/** @type {import('svelte/store').Readable<Text>} */
export const text = readable(
  function text(strings, ...values) {
    const translations = getContext(TRANSLATIONS) || []
    return translate(strings, values, translations, get(getLanguage()))
  },
  (set) => {
    getLanguage().subscribe(($language) => {
      set(createText($language))
    })
  }
)

/**
 *
 * @param {TemplateStringsArray|string} strings
 * @param {any[]} values
 * @param {Translation[]} translations
 * @param {string} language
 * @returns {string}
 */
export function translate(strings, values, translations, language) {
  const key = Array.isArray(strings) ? strings.join('%s') : String(strings)
  const numbers = values.filter((value) => typeof value === 'number')

  let value
  for (const translation of translations) {
    value = translation[language]?.[key]
    if (value) break
  }

  if (!value && import.meta.env.DEV && language !== 'en') {
    console.warn(`Missing translation for "${key}".`)
  }

  if (numbers.length) {
    if (value && typeof value !== 'string') {
      const plural = numbers.map((num) => (num === 1 ? 'one' : 'other'))
      value = value[plural.join(':')]
    } else if (import.meta.env.DEV) {
      console.warn(`Missing pluralization for "${key}".`)
    }
  }

  if (typeof value !== 'string') value = key

  // @ts-ignore
  let res = value.split('%s')
  if (res.length > 1) {
    res = res
      .reduce(function (acc, str, index, list) {
        acc.push(str)
        if (values[index] != null && index < list.length - 1) {
          acc.push(String(values[index]))
        }
        return acc
      }, /** @type {string[]} */ ([]))
      .flat()
  }

  return res.join('')
}
